import { AsyncJobResponse } from "../models/api-response";
import { AxiosRequestConfig } from "axios";
import { callExternalApi, ApiResponse } from "./external-api.service";
import { POLL_REQUEST_TIMEOUT_MS, pollJob } from "./job-polling";
import { getLogger } from "src/utils/logger";

const logger = getLogger('teaching-programs.service');
const apiServerUrl = process.env.REACT_APP_API_SERVER_URL;

export interface TeachingProgramFormData {
  programType: 'lesson_plan' | 'unit_of_work';
  curriculum: 'nsw' | 'australian' | 'vic';
  subject: string;
  stages?: string[];  // NSW/Australian only
  yearLevels?: string[];  // VIC only (Foundation, Year 1-6)
  outcomes: string;
  numberOfLessons?: number;
  // unit_of_work only — how the lesson table is laid out. Omitted means the
  // backend applies the curriculum default (vic → 'rows', otherwise 'columns').
  lessonTableOrientation?: 'columns' | 'rows';
  additionalInfo?: string;
  capabilities?: string[];  // VIC only (1-4 selections)
  crossCurriculumPriorities?: string[];  // VIC only (1-3 selections)
}

export interface TeachingProgramJobStatus {
  job_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  created_at: string;
  updated_at: string;
  progress: {
    percentage: number;
    message: string;
  };
  download_url?: string;
  document_id?: string;
  doc_base64?: string;
  error?: string;
}

/**
 * Generate teaching program document based on the provided form data
 * Returns a job ID and processes asynchronously
 *
 * @param formData The teaching program form data
 * @param accessToken The user's access token for authentication
 * @returns A promise resolving to an ApiResponse containing the job ID or an error
 */
export const generateTeachingProgram = async (
  formData: TeachingProgramFormData,
  accessToken: string
): Promise<ApiResponse<{ job_id: string }>> => {
  const config: AxiosRequestConfig = {
    url: `${apiServerUrl}/api/documents/teachingProgram`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    data: { formData },
  };

  logger.debug("Creating teaching program job", { programType: formData.programType });
  const response = await callExternalApi<AsyncJobResponse>({ config });
  logger.debug(`Response received with status: ${response.data?.status || 'unknown'}`);

  if (response.error || !response.data) {
    return {
      data: null,
      error: response.error || { message: "Failed to create teaching program job" }
    };
  }

  try {
    // Check response status (should be "accepted" for 202)
    if (response.data.status !== "accepted") {
      const errorMessage = response.data.message || "Failed to create teaching program job";
      logger.error(errorMessage);
      return {
        data: null,
        error: { message: errorMessage }
      };
    }

    // Validate job_id is present
    if (!response.data.job_id) {
      logger.error("Response missing job_id");
      return {
        data: null,
        error: { message: "Invalid response: missing job_id" }
      };
    }

    logger.debug(`Teaching program job created successfully: ${response.data.job_id}`);

    return {
      data: { job_id: response.data.job_id },
      error: null
    };
  } catch (parseError) {
    logger.error("Failed to parse response:", parseError);
    return {
      data: null,
      error: { message: "Invalid response format from API" }
    };
  }
};

/**
 * Get job status for a teaching program generation job
 *
 * @param jobId The job ID to check
 * @param accessToken The user's access token for authentication
 * @returns A promise resolving to the job status
 */
export const getJobStatus = async (
  jobId: string,
  accessToken: string
): Promise<ApiResponse<TeachingProgramJobStatus>> => {
  const config: AxiosRequestConfig = {
    url: `${apiServerUrl}/api/documents/teachingProgram/status/${jobId}`,
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    // Without a timeout a hung request stalls the polling loop forever.
    // Generous enough to ride out a Lambda cold start (~8-10s).
    timeout: POLL_REQUEST_TIMEOUT_MS,
  };

  logger.debug("Getting job status", { jobId });
  const response = await callExternalApi<TeachingProgramJobStatus>({ config });

  if (response.error || !response.data) {
    return {
      data: null,
      error: response.error || { message: "Failed to get job status" }
    };
  }

  return {
    data: response.data,
    error: null
  };
};

/**
 * Poll job status until completion
 *
 * @param jobId The job ID to poll
 * @param accessToken The user's access token for authentication
 * @param onProgress Optional callback for progress updates
 * @param pollInterval Interval in milliseconds between polls (default: 2000)
 * @param maxAttempts Maximum number of poll attempts (default: 240, 8 minutes —
 *                   shadows the 500s Lambda cap for worst-case 10-lesson runs)
 * @returns A promise resolving to the final job status
 */
export const pollJobStatus = async (
  jobId: string,
  accessToken: string,
  onProgress?: (progress: { percentage: number; message: string }) => void,
  pollInterval: number = 2000,
  maxAttempts: number = 240
): Promise<ApiResponse<TeachingProgramJobStatus>> => {
  try {
    const status = await pollJob<TeachingProgramJobStatus>({
      // getJobStatus reports failures via the ApiResponse envelope rather
      // than throwing; convert to a rejection so pollJob can retry it.
      fetchStatus: async () => {
        const statusResponse = await getJobStatus(jobId, accessToken);
        if (statusResponse.error || !statusResponse.data) {
          throw new Error(statusResponse.error?.message || 'Failed to get job status');
        }
        return statusResponse.data;
      },
      isTerminal: (status) => status.status === 'completed' || status.status === 'failed',
      onStatus: (status) => {
        if (onProgress && status.progress) {
          onProgress(status.progress);
        }
      },
      timeoutMessage: 'Teaching program generation timed out. Please try again.',
      pollInterval,
      maxAttempts
    });
    return { data: status, error: null };
  } catch (err) {
    return {
      data: null,
      error: { message: err instanceof Error ? err.message : 'Failed to get job status' }
    };
  }
};